/**
 * What the triage desk does with the answer of POST /leads/:id/verify-manually.
 *
 * 2026-10-08: the row flips to verified, and the toast says "Verified", only
 * when the server says the lead IS verified (D14: every required + visible
 * field answered). A partial save keeps the row's status and names the gaps.
 * A server too old to send `verified` keeps the previous behaviour.
 */
import type { DashboardRow } from './triageDeskTypes';

export interface VerifyManualResult {
  verifiedByName?: string | null;
  score?: number | null;
  interestLevel?: string | null;
  businessSegment?: string | null;
  verified?: boolean;
  verificationStatus?: string | null;
  missingFields?: Array<{ key: string; label: string }>;
}

const isVerified = (data: VerifyManualResult): boolean => data.verified !== false;

export function rowAfterVerify(row: DashboardRow, payload: Record<string, unknown>, data: VerifyManualResult): DashboardRow {
  const derivedSegment = data.businessSegment
    ?? (payload.applicationType ? (payload.applicationType === 'B2B' ? 'B2B' : 'B2C') : null);
  return {
    ...row,
    ...(payload.name            ? { name: String(payload.name) } : {}),
    ...(payload.location        ? { location: String(payload.location) } : {}),
    ...(payload.locationState   ? { state: String(payload.locationState) } : {}),
    ...(payload.product         ? { requirement: String(payload.product) } : {}),
    ...(payload.applicationType ? { applicationType: String(payload.applicationType) } : {}),
    ...(derivedSegment          ? { businessSegment: derivedSegment } : {}),
    ...(payload.leadCapacity    ? { capacity: String(payload.leadCapacity) } : {}),
    ...(payload.leadTimeline    ? { timeline: String(payload.leadTimeline) } : {}),
    ...(payload.indicativePriceShared === true ? { priceShared: true } : {}),
    ...(payload.indicativePriceRange ? { priceRange: String(payload.indicativePriceRange) } : {}),
    ...(data.score !== undefined && data.score !== null ? { score: data.score } : {}),
    ...(data.interestLevel ? { tier: data.interestLevel as DashboardRow['tier'] } : {}),
    ...(isVerified(data)
      ? { verifiedStatus: 'verified' as const, verifiedBy: 'manual' as const, verifiedByName: data.verifiedByName ?? null }
      : {}),
  };
}

export function verifyToastText(who: string, data: VerifyManualResult): string {
  if (isVerified(data)) return `Verified ${who}`;
  const missing = (data.missingFields ?? []).map((f) => f.label).filter(Boolean);
  return missing.length > 0
    ? `Answers saved for ${who} — still needed: ${missing.join(', ')}`
    : `Answers saved for ${who} — not verified yet`;
}
